import { useState, useEffect, useCallback } from 'react';
import { Search, Filter, X, Home, Building, Briefcase, Map, Sofa } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { PropertyFilters } from '@/types/property';
import { useLanguage } from '@/hooks/useLanguage';

// Mapeo de iconos disponibles
const iconMap = {
  'Home': Home,
  'Building': Building,
  'Briefcase': Briefcase,
  'Map': Map,
  'Sofa': Sofa
};

interface FiltersConfig {
  enabled?: boolean;
  showQuickFilters?: boolean;
  defaultFilters?: PropertyFilters;
  quickFilters?: Array<{
    label: string;
    value: string;
    icon: string;
  }>;
}

interface PropertySearchProps {
  onSearch: (filters: PropertyFilters) => void;
  initialFilters?: PropertyFilters;
  filtersConfig?: FiltersConfig;
  loading?: boolean;
}

export function PropertySearch({
  onSearch,
  initialFilters = {},
  filtersConfig,
  loading = false
}: PropertySearchProps) {
  const [filters, setFilters] = useState<PropertyFilters>(initialFilters);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(initialFilters.search || '');
  const { t } = useLanguage();

  // Debounce search: trigger onSearch 300ms after user stops typing
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm !== filters.search) {
        const newFilters = { ...filters, search: searchTerm || undefined };
        setFilters(newFilters);
        onSearch(newFilters);
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [searchTerm]); // Only re-run when searchTerm changes

  // Si los filtros están deshabilitados, no mostrar nada
  if (filtersConfig?.enabled === false) {
    return null;
  }

  // Mostrar loading state si está cargando la configuración
  if (loading) {
    return (
      <div className="bg-white p-6 rounded-xl shadow-lg border animate-pulse">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 h-10 bg-gray-200 rounded"></div>
          <div className="flex gap-3">
            <div className="w-32 h-10 bg-gray-200 rounded"></div>
            <div className="w-32 h-10 bg-gray-200 rounded"></div>
            <div className="w-20 h-10 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const handleFilterChange = (key: keyof PropertyFilters, value: any) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
  };

  const handleQuickFilterClick = (filterValue: string) => {
    const newFilters = { ...filters, type: filterValue };
    setFilters(newFilters);
    onSearch(newFilters);
  };

  const handleSearch = () => {
    onSearch(filters);
    setIsFilterOpen(false);
  };

  const clearFilters = () => {
    const defaultFilters = filtersConfig?.defaultFilters || {};
    setFilters(defaultFilters);
    setSearchTerm(''); // Clear search term
    onSearch(defaultFilters);
  };

  const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
    const defaultValue = filtersConfig?.defaultFilters?.[key as keyof PropertyFilters];
    return value !== undefined && value !== '' && value !== defaultValue;
  });

  return (
    <div className="space-y-4">
      {/* Quick Filters - Solo si están habilitados */}
      {filtersConfig?.showQuickFilters && filtersConfig?.quickFilters && (
        <div className="flex flex-wrap gap-3 justify-center">
          {filtersConfig.quickFilters.map((filter) => {
            const IconComponent = iconMap[filter.icon as keyof typeof iconMap] || Home;
            const isActive = filters.type === filter.value;
            
            return (
              <Button
                key={filter.value}
                variant={isActive ? "default" : "outline"}
                onClick={() => handleQuickFilterClick(filter.value)}
                className="flex items-center gap-2"
              >
                <IconComponent className="h-4 w-4" />
                {filter.label}
              </Button>
            );
          })}
        </div>
      )}

      {/* Main Search Bar */}
      <div className="bg-white p-6 rounded-xl shadow-lg border">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search Input */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder={t('properties.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-10"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label={t('properties.clearSearch')}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Basic Filters */}
          <div className="flex flex-wrap lg:flex-nowrap gap-3">
            <Select
              value={filters.operation || 'all-operations'}
              onValueChange={(value) => handleFilterChange('operation', value === 'all-operations' ? undefined : value)}
            >
              <SelectTrigger className="w-full lg:w-[140px]">
                <SelectValue placeholder={t('properties.operation')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-operations">{t('properties.all')}</SelectItem>
                <SelectItem value="venta">{t('properties.sale')}</SelectItem>
                <SelectItem value="arriendo">{t('properties.rent')}</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={filters.type || 'all-types'}
              onValueChange={(value) => handleFilterChange('type', value === 'all-types' ? undefined : value)}
            >
              <SelectTrigger className="w-full lg:w-[140px]">
                <SelectValue placeholder={t('properties.type')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-types">{t('properties.allTypes')}</SelectItem>
                {/* Usar los filtros dinámicos del JSON si están disponibles */}
                {filtersConfig?.quickFilters ? (
                  filtersConfig.quickFilters.map((filter) => (
                    <SelectItem key={filter.value} value={filter.value}>
                      {filter.label}
                    </SelectItem>
                  ))
                ) : (
                  <>
                    <SelectItem value="casa">Casa</SelectItem>
                    <SelectItem value="departamento">Departamento</SelectItem>
                    <SelectItem value="oficina">Oficina</SelectItem>
                    <SelectItem value="terreno">Terreno</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>

            {/* Advanced Filters Sheet */}
            <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" className="relative">
                  <Filter className="h-4 w-4 mr-2" />
                  {t('properties.filters')}
                  {hasActiveFilters && (
                    <span className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full" />
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle>{t('properties.advancedFilters')}</SheetTitle>
                </SheetHeader>
                
                <div className="space-y-6 mt-6">
                  {/* Price Range */}
                  <div className="space-y-3">
                    <Label>{t('properties.priceRange')}</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Input
                          type="number"
                          placeholder={t('properties.minimum')}
                          value={filters.minPrice || ''}
                          onChange={(e) => handleFilterChange('minPrice', e.target.value ? parseInt(e.target.value) : undefined)}
                        />
                      </div>
                      <div>
                        <Input
                          type="number"
                          placeholder={t('properties.maximum')}
                          value={filters.maxPrice || ''}
                          onChange={(e) => handleFilterChange('maxPrice', e.target.value ? parseInt(e.target.value) : undefined)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Bedrooms & Bathrooms */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('properties.bedrooms')}</Label>
                      <Select
                        value={filters.bedrooms?.toString() || 'any-bedrooms'}
                        onValueChange={(value) => handleFilterChange('bedrooms', value === 'any-bedrooms' ? undefined : parseInt(value))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('properties.any')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any-bedrooms">{t('properties.any')}</SelectItem>
                          <SelectItem value="1">1+</SelectItem>
                          <SelectItem value="2">2+</SelectItem>
                          <SelectItem value="3">3+</SelectItem>
                          <SelectItem value="4">4+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>{t('properties.bathrooms')}</Label>
                      <Select
                        value={filters.bathrooms?.toString() || 'any-bathrooms'}
                        onValueChange={(value) => handleFilterChange('bathrooms', value === 'any-bathrooms' ? undefined : parseInt(value))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t('properties.any')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any-bathrooms">{t('properties.any')}</SelectItem>
                          <SelectItem value="1">1+</SelectItem>
                          <SelectItem value="2">2+</SelectItem>
                          <SelectItem value="3">3+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Area Range */}
                  <div className="space-y-3">
                    <Label>{t('properties.surface')}</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        type="number"
                        placeholder={t('properties.minArea')}
                        value={filters.minArea || ''}
                        onChange={(e) => handleFilterChange('minArea', e.target.value ? parseInt(e.target.value) : undefined)}
                      />
                      <Input
                        type="number"
                        placeholder={t('properties.maxArea')}
                        value={filters.maxArea || ''}
                        onChange={(e) => handleFilterChange('maxArea', e.target.value ? parseInt(e.target.value) : undefined)}
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3 pt-6">
                    <Button onClick={handleSearch} className="w-full">
                      {t('properties.applyFilters')}
                    </Button>
                    {hasActiveFilters && (
                      <Button variant="outline" onClick={clearFilters} className="w-full">
                        <X className="h-4 w-4 mr-2" />
                        {t('properties.clearFilters')}
                      </Button>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>

            <Button onClick={handleSearch} className="px-6">
              <Search className="h-4 w-4 mr-2" />
              {t('common.search')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}